import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useI18n } from "@/i18n/useI18n";

interface InterventionTabsProps {
  publicContent: ReactNode;
  /** Private questionnaire content; `null` when the user may not see it. */
  privateContent: ReactNode | null;
  privateFirst?: boolean;
  /** Notifies the editor which section the shared Save button should persist. */
  onTabChange?: (tab: "public" | "private") => void;
  /** Shows only the questionnaire (respondents without read access). */
  hidePublic?: boolean;
  /** With `hidePublic`: keep the lone questionnaire tab header (details view). */
  showLoneTab?: boolean;
  /** Shown at the right end of the tab row (the status badge). */
  aside?: ReactNode;
}

/**
 * Public "display information" vs. private "questionnaire" tabs. With
 * `hidePublic` only the questionnaire is shown (with its tab header when
 * `showLoneTab`); the public content stays mounted but hidden so form state is kept.
 */
export function InterventionTabs({
  publicContent,
  privateContent,
  privateFirst = false,
  onTabChange,
  hidePublic = false,
  showLoneTab = false,
  aside,
}: InterventionTabsProps) {
  const { t } = useI18n();
  const id = useId();
  const [selected, setSelected] = useState<"public" | "private">(
    privateFirst ? "private" : "public",
  );
  const tabRefs = useRef<Record<"public" | "private", HTMLButtonElement | null>>({
    public: null,
    private: null,
  });

  // Without a tab row the aside gets a row of its own.
  const asideRow = aside && <div className="intervention-tabs-aside-row">{aside}</div>;

  if (!privateContent)
    return (
      <>
        {asideRow}
        {hidePublic ? <div hidden>{publicContent}</div> : publicContent}
      </>
    );

  if (hidePublic && !showLoneTab) {
    return (
      <>
        {asideRow}
        <div hidden>{publicContent}</div>
        <p className="questionnaire-help">{t("formDataNotice")}</p>
        {privateContent}
      </>
    );
  }

  const current = hidePublic ? "private" : selected;
  const tabs = [
    ...(hidePublic ? [] : [{ key: "public" as const, label: t("formDisplayInformationTab") }]),
    { key: "private" as const, label: t("formDataTab") },
  ];
  const onKeyDown = (event: KeyboardEvent) => {
    if (hidePublic) return;
    const next =
      event.key === "ArrowRight" || event.key === "ArrowLeft"
        ? selected === "public"
          ? "private"
          : "public"
        : event.key === "Home"
          ? "public"
          : event.key === "End"
            ? "private"
            : null;
    if (!next) return;
    event.preventDefault();
    setSelected(next);
    onTabChange?.(next);
    tabRefs.current[next]?.focus();
  };

  const tablist = (
    <div className="intervention-tabs">
      <div
        className="intervention-tablist"
        role="tablist"
        aria-label={t("formDisplayInformationTab")}
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            ref={(element) => {
              tabRefs.current[tab.key] = element;
            }}
            type="button"
            role="tab"
            id={`${id}-${tab.key}-tab`}
            aria-controls={`${id}-${tab.key}`}
            aria-selected={current === tab.key}
            tabIndex={current === tab.key ? 0 : -1}
            onClick={() => {
              setSelected(tab.key);
              onTabChange?.(tab.key);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {aside && <div className="intervention-tabs-aside">{aside}</div>}
    </div>
  );

  return (
    <>
      {tablist}
      {hidePublic ? (
        <div hidden>{publicContent}</div>
      ) : (
        <div
          role="tabpanel"
          id={`${id}-public`}
          aria-labelledby={`${id}-public-tab`}
          hidden={current !== "public"}
        >
          {publicContent}
        </div>
      )}
      <div
        role="tabpanel"
        id={`${id}-private`}
        aria-labelledby={`${id}-private-tab`}
        hidden={current !== "private"}
      >
        <p className="questionnaire-help">{t("formDataNotice")}</p>
        {privateContent}
      </div>
    </>
  );
}
